import { medimo } from "@medimo/core-api/internal/next";

// 未送信の closed イベントはタブを閉じても残す必要があるため localStorage に置く。
// sessionStorage はタブと一緒に消えるので、取りこぼしの主因である
// 「閉じた直後にタブを閉じる」を救えない
const PENDING_CLOSED_KEY = "releaseNotificationPendingClosed";
// 期限切れの残骸を無期限に送り続けないための保持期間
const PENDING_CLOSED_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type ClosedEventPayload = {
  notificationId: string;
  loginAttemptId: string;
  viewDurationMs: number;
  action: "later" | "close";
  savedAt: number;
};

const readPending = (): ClosedEventPayload | null => {
  try {
    const raw = localStorage.getItem(PENDING_CLOSED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ClosedEventPayload>;
    if (
      typeof parsed.notificationId !== "string" ||
      typeof parsed.loginAttemptId !== "string" ||
      typeof parsed.viewDurationMs !== "number" ||
      (parsed.action !== "later" && parsed.action !== "close") ||
      typeof parsed.savedAt !== "number"
    ) {
      return null;
    }
    return parsed as ClosedEventPayload;
  } catch {
    return null;
  }
};

const clearPending = () => {
  try {
    localStorage.removeItem(PENDING_CLOSED_KEY);
  } catch (e) {
    console.error("closedイベントの保留データを削除できませんでした:", e);
  }
};

const savePending = (payload: ClosedEventPayload) => {
  try {
    localStorage.setItem(PENDING_CLOSED_KEY, JSON.stringify(payload));
  } catch (e) {
    console.error("closedイベントの保留データを保存できませんでした:", e);
  }
};

/**
 * closed イベントを送信する。
 *
 * サーバーが受理して初めて3秒未満クローズが記録され、2回目の表示が可能になる。
 * 送信前に localStorage へ保留データを書き、HTTP 応答を得たら削除する。
 * 応答を得られなかった場合だけ保留データが残り、次回起動時に再送する。
 * keepalive を付けるのは、送信直後にタブを閉じても配送を続けさせるため
 */
const postClosed = async (payload: ClosedEventPayload) => {
  try {
    const { response } = await medimo.POST(
      "/v2/notifications/{notification_id}/modal-events",
      {
        params: { path: { notification_id: payload.notificationId } },
        body: {
          event: "closed",
          login_attempt_id: payload.loginAttemptId,
          view_duration_ms: payload.viewDurationMs,
          action: payload.action,
        },
        keepalive: true,
      },
    );
    // 4xx はサーバーが判断を下した終端なので、再送しても結果は変わらない
    if (response) clearPending();
  } catch (e) {
    console.error("モーダルのcloseイベント送信に失敗しました:", e);
  }
};

export const sendClosedEvent = (
  payload: Omit<ClosedEventPayload, "savedAt">,
) => {
  const stored: ClosedEventPayload = { ...payload, savedAt: Date.now() };
  savePending(stored);
  postClosed(stored);
};

/** 前回の起動で送信しきれなかった closed イベントを再送する */
export const flushPendingClosedEvent = () => {
  const pending = readPending();
  if (!pending) return;
  if (Date.now() - pending.savedAt > PENDING_CLOSED_MAX_AGE_MS) {
    clearPending();
    return;
  }
  postClosed(pending);
};

/** 既読化に成功した通知は closed の再送が不要になる */
export const clearPendingClosedEventFor = (notificationId: string) => {
  const pending = readPending();
  if (pending?.notificationId === notificationId) clearPending();
};
